/**
 * ACQUISITION V2 — parcours organique first-party, ANONYME.
 *
 * Doctrine PO : mesurer être découvert → lu → crédible → exploré → contacté,
 * SANS bloc visible ajouté, SANS cookie, SANS outil tiers, SANS identifiant.
 * L'état de session (source, page d'atterrissage, premier contenu) ne vit
 * que dans le sessionStorage du lecteur : le serveur ne reçoit que des
 * incréments agrégés par jour (voir backend/routes/acquisition.py).
 * Fail-silent absolu : la mesure ne gêne jamais la lecture.
 */

const API = process.env.REACT_APP_BACKEND_URL;
const KEY = 'wm_journey_v1';

/* ENGINE V2 — FIRST TOUCH persistant (attribution multi-session).
 * Contenu STRICTEMENT NON-PII : source, engine, landing, utm, date de pose.
 * Aucun identifiant, aucun fingerprinting. Conservé en localStorage
 * first-party pendant FIRST_TOUCH_TTL_DAYS puis expiré ; transmis au
 * serveur UNIQUEMENT à l'identification volontaire (journeySnapshot).
 * Un visiteur qui purge son navigateur perd son first touch : comportement
 * accepté et documenté (aucun contournement). */
export const FIRST_TOUCH_TTL_DAYS = 180;
const FT_KEY = 'wm_first_touch_v1';

function readFirstTouch() {
  try {
    const raw = JSON.parse(localStorage.getItem(FT_KEY));
    if (!raw || !raw.at) return null;
    const ageDays = (Date.now() - new Date(raw.at).getTime()) / 86400000;
    if (ageDays > FIRST_TOUCH_TTL_DAYS || ageDays < 0) {
      localStorage.removeItem(FT_KEY);
      return null;
    }
    return raw;
  } catch { return null; }
}

function writeFirstTouchOnce(touch) {
  try {
    if (!readFirstTouch()) localStorage.setItem(FT_KEY, JSON.stringify(touch));
  } catch { /* stockage indisponible : first touch de session seulement */ }
}

// Referrers IA testés AVANT les moteurs (gemini.google.com ≠ google.com).
const AI_HOSTS = /chatgpt\.com|chat\.openai\.com|perplexity\.ai|claude\.ai|gemini\.google\.com|copilot\.microsoft\.com|you\.com|phind\.com|poe\.com|mistral\.ai/;
const ENGINE_HOSTS = /google\.|bing\.|duckduckgo|qwant|ecosia|yahoo\.|startpage|brave\./;

// V3 — détail du canal quand le referrer le donne FIABLEMENT (le trafic IA
// sans referrer est invisible : il n'est PAS estimé, doctrine PO).
const ENGINE_DETAIL = [
  ['chatgpt', /chatgpt\.com|chat\.openai\.com/],
  ['perplexity', /perplexity\.ai/],
  ['claude', /claude\.ai/],
  ['gemini', /gemini\.google\.com/],
  ['copilot', /copilot\.microsoft\.com/],
  ['google', /google\./],
  ['bing', /bing\./],
  ['duckduckgo', /duckduckgo/],
  ['qwant', /qwant/],
  ['ecosia', /ecosia/],
  ['yahoo', /yahoo\./],
];

export function engineDetail() {
  try {
    const r = document.referrer;
    if (!r) return null;
    const host = new URL(r).host;
    if (host === window.location.host) return null;
    for (const [name, pattern] of ENGINE_DETAIL) {
      if (pattern.test(host)) return name;
    }
    if (AI_HOSTS.test(host) || ENGINE_HOSTS.test(host)) return 'autre';
    return null;
  } catch { return null; }
}

function state() {
  try { return JSON.parse(sessionStorage.getItem(KEY)) || {}; } catch { return {}; }
}
function save(s) {
  try { sessionStorage.setItem(KEY, JSON.stringify(s)); } catch { /* privé/plein */ }
}

function send(payload) {
  try {
    const url = `${API}/api/acquisition/public/journey`;
    const body = JSON.stringify(payload);
    const ok = navigator.sendBeacon
      && navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    if (!ok) {
      fetch(url, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body, keepalive: true,
      }).catch(() => {});
    }
  } catch { /* mesure silencieuse */ }
}

export function sourceBucket() {
  try {
    const r = document.referrer;
    if (!r) return 'direct';
    const u = new URL(r);
    if (u.host === window.location.host) return 'interne';
    if (AI_HOSTS.test(u.host)) return 'ia';
    if (ENGINE_HOSTS.test(u.host)) return 'moteur';
    return 'externe';
  } catch { return 'direct'; }
}

/** Première page de la session : source + atterrissage (une fois).
 *  V3 : + détail moteur/assistant quand le referrer le donne fiablement.
 *  ENGINE V1 : + UTM first-touch (jamais écrasés, §29) — conservés
 *  UNIQUEMENT dans le sessionStorage : transmis au serveur seulement si
 *  le visiteur s'identifie volontairement via un formulaire (§4). */
export function trackVisit(pathname) {
  const s = state();
  if (s.visited) return;
  s.visited = true;
  s.source = sourceBucket();
  s.landing = pathname;
  s.engine = engineDetail();
  s.session_at = new Date().toISOString(); // ENGINE V2 — date du last touch
  try {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']
      .forEach((k) => { const v = params.get(k); if (v) utm[k] = v.slice(0, 140); });
    if (Object.keys(utm).length) s.utm = utm; // first-touch : posé une fois
  } catch { /* mesure silencieuse */ }
  save(s);
  // ENGINE V2 — first touch persistant : posé UNE fois, jamais écrasé.
  writeFirstTouchOnce({
    source: s.source, engine: s.engine || undefined, landing: pathname,
    utm: s.utm || undefined, at: s.session_at,
  });
  const payload = { kind: 'visit', source: s.source, landing: pathname };
  if (s.engine && (s.source === 'moteur' || s.source === 'ia')) payload.engine = s.engine;
  send(payload);
}

/** Contenu ouvert (article ou guide) : pose le premier contenu, mémorise le
 *  dernier consulté (V3), puis signale l'EXPLORATION au 2e contenu distinct
 *  (une fois par session) en portant AUSSI ce deuxième contenu (V3). */
export function trackContentView(contentSlug) {
  if (!contentSlug) return;
  const s = state();
  s.last_content = contentSlug; // V3 — « dernier contenu avant l'acte »
  // ENGINE V1 — liste bornée des contenus consultés (sessionStorage
  // uniquement — transmise au lead à l'identification volontaire, §4).
  const seen = Array.isArray(s.contents) ? s.contents : [];
  if (!seen.includes(contentSlug) && seen.length < 20) {
    s.contents = [...seen, contentSlug];
  }
  if (!s.first_content) {
    s.first_content = contentSlug;
    save(s);
    return;
  }
  if (contentSlug !== s.first_content && !s.explored) {
    s.explored = true;
    save(s);
    send({ kind: 'explore', source: s.source || 'direct',
           first_content: s.first_content, second_content: contentSlug });
    return;
  }
  save(s);
}

/** Lecture qualifiée (≥ 10 s, relayé par useEditorialPulse) — une fois.
 *  V3 : porte le contenu réellement lu (répond à « est-il lu ? »). */
export function trackRead(contentSlug) {
  const s = state();
  if (s.read_sent) return;
  s.read_sent = true;
  save(s);
  send({ kind: 'read', source: s.source || 'direct',
         content: contentSlug || s.first_content || '',
         first_content: s.first_content || contentSlug || '' });
}

/** Cible d'intention commerciale d'un lien interne — ou null. */
export function intentTarget(href) {
  const h = String(href || '');
  if (/^\/(fr|en)\/contact(?:[/?#]|$)/.test(h)) return 'contact';
  if (/^\/(fr|en)\/(comment-ca-marche|how-it-works)(\/|$)/.test(h)) return 'methode';
  if (/^\/(fr|en)\/(implantations|locations)\//.test(h)) return 'implantation';
  if (/^\/(fr|en)\/(realisations|track-record)(\/|$)/.test(h)) return 'realisation';
  if (/^\/(fr|en)\/(demander-acces|request-access)(\/|$)/.test(h)) return 'acces';
  return null;
}

export function trackIntent(target) {
  const s = state();
  s.last_intent_target = target; // V3 — CTA déclencheur si conversion ensuite
  save(s);
  send({ kind: 'intent', source: s.source || 'direct',
         first_content: s.first_content || '', target,
         last_content: s.last_content || '' });
}

/** Conversion : 'start' (premier champ touché, dédupliqué) · 'submit' (envoi
 *  réussi). V3 : porte le formulaire (contact|acces), l'atterrissage de la
 *  session, le dernier contenu consulté et le CTA déclencheur — uniquement
 *  ce que la session first-party connaît réellement. */
export function trackConversion(stage, form) {
  const s = state();
  if (stage === 'start') {
    if (s.conv_started) return;
    s.conv_started = true;
    save(s);
  }
  const payload = {
    kind: stage === 'submit' ? 'convert_submit' : 'convert_start',
    source: s.source || 'direct', first_content: s.first_content || '',
  };
  if (stage === 'submit') {
    if (form) payload.form = form;
    if (s.landing) payload.landing = s.landing;
    if (s.last_content) payload.last_content = s.last_content;
    if (s.last_intent_target) payload.trigger = s.last_intent_target;
  }
  send(payload);
}

/** ENGINE V1 — instantané du parcours pour la RÉCONCILIATION anonyme →
 *  identifié (§4). Appelé PAR les formulaires au moment de la soumission
 *  volontaire : le visiteur transmet lui-même son parcours (le serveur ne
 *  stocke aucune session anonyme — aucun backfill possible, §50).
 *  INTERDITS respectés : pas de fingerprinting, pas d'identifiant caché,
 *  rien n'est envoyé avant l'acte volontaire. */
export function journeySnapshot() {
  try {
    const s = state();
    if (!s.visited) return null;
    // ENGINE V2 — attribution multi-touch : first touch persistant (ou la
    // session courante si première visite), last touch = session courante.
    const lastTouch = {
      source: s.source || 'direct',
      engine: s.engine || undefined,
      landing: s.landing || undefined,
      utm: s.utm || undefined,
      at: s.session_at || undefined,
    };
    const firstTouch = readFirstTouch() || lastTouch;
    return {
      source: s.source || 'direct',
      engine: s.engine || undefined,
      landing: s.landing || undefined,
      first_content: s.first_content || undefined,
      last_content: s.last_content || undefined,
      contents: Array.isArray(s.contents) ? s.contents.slice(0, 20) : [],
      trigger: s.last_intent_target || undefined,
      explored: Boolean(s.explored),
      read: Boolean(s.read_sent),
      utm: s.utm || undefined,
      first_touch: firstTouch,
      last_touch: lastTouch,
    };
  } catch { return null; }
}
